/**
 * How this viewer is looking at the call (issues #123 and #124, epic #122 §6.3).
 *
 * A per-viewer view setting: nothing here is sent to the server or to
 * LiveKit, and nothing is remembered in `localStorage` (there is one layout,
 * so there is nothing to remember). React owns it; Phaser never sees it.
 *
 * It holds the mode (the corner tiles or full screen) and the pin (#124): at
 * most one tile, by its `tileKey` (`identity:source`, so a camera and a
 * screen share pin separately). The overlay reports the call's tiles with
 * `tiles_changed`, which is how a pin whose tile has gone is cleared and how
 * a new screen share pins itself (epic D10).
 */

/** The corner tiles over the world, or the full-screen overlay. */
export type VideoViewMode = 'hud' | 'fullscreen';

export interface VideoViewState {
  mode: VideoViewMode;

  /** The pinned tile's `tileKey`, or `null`: the tiled layout. One at a time. */
  pinnedKey: string | null;

  /**
   * Live screen-share tile keys, in the order they appeared in this client
   * (oldest first), so "the most recent share" has a meaning.
   */
  shareKeys: string[];

  /**
   * Live shares the viewer took the pin away from (unpinned, or pinned
   * something else over). They never pin themselves again while they last,
   * and are forgotten when they end.
   */
  dismissedShareKeys: string[];
}

export type VideoViewAction =
  | { type: 'enter_fullscreen' }
  | { type: 'exit_fullscreen' }
  | { type: 'pin'; key: string }
  | { type: 'unpin' }
  /** The call's tiles, every key and the screen-share keys among them, in the stable order */
  | { type: 'tiles_changed'; keys: readonly string[]; shareKeys: readonly string[] }
  /** Back to the world with nothing pinned: the call is over */
  | { type: 'call_ended' };

export const initialVideoViewState: VideoViewState = {
  mode: 'hud',
  pinnedKey: null,
  shareKeys: [],
  dismissedShareKeys: []
};

/** The most recent live share that may pin itself, if any. */
function latestPinnableShare(state: VideoViewState): string | null {
  for (let index = state.shareKeys.length - 1; index >= 0; index--) {
    const key = state.shareKeys[index];
    if (!state.dismissedShareKeys.includes(key)) return key;
  }
  return null;
}

/** The dismissed shares once the pin leaves the current one by the viewer's hand. */
function dismissPinnedShare(state: VideoViewState): string[] {
  const { pinnedKey, shareKeys, dismissedShareKeys } = state;
  if (pinnedKey === null || !shareKeys.includes(pinnedKey) || dismissedShareKeys.includes(pinnedKey)) {
    return dismissedShareKeys;
  }
  return [...dismissedShareKeys, pinnedKey];
}

/** `next` when it differs from `current` in content, otherwise `current` itself. */
function sameOr(current: string[], next: string[]): string[] {
  return current.length === next.length && current.every((key, index) => key === next[index])
    ? current
    : next;
}

/**
 * Keeps the pin in step with the tiles.
 *
 * - A pin whose tile has gone (the participant left, the share ended) clears,
 *   and the layout goes back to tiled.
 * - Shares that ended are forgotten, dismissed or not; new ones are added
 *   after the ones already live, so the last is the most recent.
 * - In full screen, a share that appears while nothing is pinned pins itself
 *   (epic D10). While something is pinned it does not take the pin: the top
 *   bar offers it instead ({@link shareToOffer}).
 */
function syncTiles(
  state: VideoViewState,
  keys: readonly string[],
  shareKeys: readonly string[]
): VideoViewState {
  const live = new Set(keys);
  const liveShares = new Set(shareKeys);

  const appeared = shareKeys.filter((key) => !state.shareKeys.includes(key));
  const nextShareKeys = sameOr(state.shareKeys, [
    ...state.shareKeys.filter((key) => liveShares.has(key)),
    ...appeared
  ]);
  const nextDismissed = sameOr(
    state.dismissedShareKeys,
    state.dismissedShareKeys.filter((key) => liveShares.has(key))
  );

  let pinnedKey = state.pinnedKey !== null && live.has(state.pinnedKey) ? state.pinnedKey : null;
  if (pinnedKey === null && state.mode === 'fullscreen' && appeared.length > 0) {
    pinnedKey = appeared[appeared.length - 1];
  }

  if (
    pinnedKey === state.pinnedKey &&
    nextShareKeys === state.shareKeys &&
    nextDismissed === state.dismissedShareKeys
  ) {
    return state;
  }

  return { ...state, pinnedKey, shareKeys: nextShareKeys, dismissedShareKeys: nextDismissed };
}

export function videoViewReducer(state: VideoViewState, action: VideoViewAction): VideoViewState {
  switch (action.type) {
    case 'enter_fullscreen':
      if (state.mode === 'fullscreen') return state;
      // A share that is already live pins itself on the way in, unless
      // something is pinned or the viewer has unpinned it (epic D10).
      return {
        ...state,
        mode: 'fullscreen',
        pinnedKey: state.pinnedKey ?? latestPinnableShare(state)
      };

    case 'exit_fullscreen':
      return state.mode === 'hud' ? state : { ...state, mode: 'hud' };

    case 'pin':
      if (state.pinnedKey === action.key) return state;
      // Swapping away from a share dismisses it, as unpinning it would;
      // pinning a dismissed share by hand takes it off that list.
      return {
        ...state,
        pinnedKey: action.key,
        dismissedShareKeys: dismissPinnedShare(state).filter((key) => key !== action.key)
      };

    case 'unpin':
      if (state.pinnedKey === null) return state;
      return { ...state, pinnedKey: null, dismissedShareKeys: dismissPinnedShare(state) };

    case 'tiles_changed':
      return syncTiles(state, action.keys, action.shareKeys);

    case 'call_ended':
      return state.mode === 'hud' &&
        state.pinnedKey === null &&
        state.shareKeys.length === 0 &&
        state.dismissedShareKeys.length === 0
        ? state
        : initialVideoViewState;

    default:
      return state;
  }
}

/**
 * While something is pinned, the most recent live screen share that is not:
 * what the top bar's "<name> is sharing · Pin" chip offers. `null` when
 * nothing is pinned (a share then pins itself, or is a tile like any other)
 * or when no other share is live.
 */
export function shareToOffer(state: VideoViewState): string | null {
  if (state.pinnedKey === null) return null;
  for (let index = state.shareKeys.length - 1; index >= 0; index--) {
    const key = state.shareKeys[index];
    if (key !== state.pinnedKey) return key;
  }
  return null;
}

/** The parts of the video call {@link isCallLive} looks at. */
export interface CallPresence {
  room: unknown;
  isReconnecting?: boolean;
  currentPrivateArea?: unknown;
}

/**
 * Whether there is a call to show full screen.
 *
 * While a room exists, yes. Without one, only while a reconnect is under way
 * for the area the player is still in: a reconnect keeps full screen open
 * with the reconnecting panel, anything else (the area left, a teleport, a
 * kick, a disconnect that is not retried, the room gone) has ended the call.
 */
export function isCallLive({ room, isReconnecting, currentPrivateArea }: CallPresence): boolean {
  if (room) return true;
  return Boolean(isReconnecting) && Boolean(currentPrivateArea);
}
